import { alignerOngletSurIdentite } from '@betool/identity-runtime'
import { entrerParPreuve } from '../api/auth'
import { ApiError } from '../api/client'
import type { Session } from '../api/client'

import {
  LancementRefuseError,
  PorteurMortError,
  acheterPreuve,
  origineMoteurIdentite,
} from './bff'
// Import DIFFÉRÉ dans la fonction : `runtime.ts` importe ce module, et un
// import statique ici créerait un cycle au chargement de la page.

/**
 * L'ENTRÉE dans betool-sign depuis le portail beTool, en trois gestes.
 *
 * LE PARCOURS
 * ---------------------------------------------------------------------------
 *   1. la PORTE envoie ici avec le laissez-passer — une INTENTION, à usage
 *      unique ;
 *   2. on la consomme AU MOTEUR, qui rend une session beTool ;
 *   3. on l'échange contre une session de ce service.
 *
 * POURQUOI AU MOTEUR, ET PLUS PAR LE BFF
 * ---------------------------------------------------------------------------
 * L'intention passait par le BFF du trousseau, qui la consommait et rangeait
 * le slot ; on achetait ensuite la preuve courte de ce slot. Deux choses ont
 * rendu ce chemin impossible (ADR-016 §14.1) :
 *
 *   * le trousseau est un cookie `__Host-` en `SameSite=Strict`. Depuis notre
 *     origine, il ne part PAS avec la requête : le BFF rangeait donc le slot
 *     dans un trousseau qui n'était pas celui de la personne ;
 *   * la preuve d'un slot porte l'audience de l'API du backoffice. Ce service
 *     exige la sienne et la refusait en 401 — mesuré en production, boucle
 *     complète : preuve obtenue (200), session refusée (401), retour à la
 *     porte, et ainsi de suite.
 *
 * Consommée au moteur, la session porte les DEUX audiences (le lancement a été
 * émis pour cette application) et l'échange aboutit. Le trousseau n'y perd
 * rien : depuis ADR-016, c'est la PORTE qui range le slot, pas l'application —
 * elle n'a jamais eu à le faire.
 *
 * L'INTENTION EST À USAGE UNIQUE, ET C'EST STRUCTURANT
 * ---------------------------------------------------------------------------
 * Un rechargement de page rejoue le même jeton, que le BFF refuse — à raison.
 * L'URL doit donc être nettoyée dès la remise faite, sinon le premier F5 après
 * une entrée réussie affiche « lancement refusé » alors que tout va bien.
 */

/** Ce que l'appelant doit MONTRER, selon ce qui s'est passé. */
export type EchecEntree =
  /** L'intention a été rejouée, ou elle a expiré : refaire le geste depuis le portail. */
  | { genre: 'intention_refusee'; code: string }
  /** Le compte n'est plus dans le trousseau, ou sa session est close. */
  | { genre: 'porteur_mort'; code: string }
  /** Aucun accès dans cette organisation : le rattachement manque. */
  | { genre: 'acces_refuse' }
  /** Une PANNE. Elle se réessaie ; elle ne se maquille pas en refus. */
  | { genre: 'panne'; message: string }

export type ResultatEntree =
  | { ok: true; session: Session }
  | { ok: false; echec: EchecEntree }

/** Le paramètre d'URL que pose le portail beTool. */
export const PARAM_LANCEMENT = 'lancement'

/**
 * Lit le jeton de lancement dans l'URL courante, et le RETIRE.
 *
 * DEUX FORMES, ET IL FAUT LES DEUX
 * ---------------------------------------------------------------------------
 * Le jeton arrive par l'un ou l'autre de deux chemins, qui ne le posent pas au
 * même endroit :
 *
 *   * depuis une TUILE du portail : `…/?lancement=<jeton>` — une query ;
 *   * depuis le SÉLECTEUR de comptes du BFF : `…#lancement=<jeton>` — un
 *     FRAGMENT, et c'est voulu : une query partirait dans les journaux d'accès
 *     du serveur de destination et dans son `Referer`, le fragment ne quitte
 *     jamais le navigateur.
 *
 * N'en lire qu'une revient à ignorer la moitié des arrivées, en silence : la
 * page s'affiche normalement et dit « connectez-vous », alors que le jeton est
 * là, dans la barre d'adresse.
 *
 * Le retrait se fait par `replaceState` : sans lui, le jeton reste dans
 * l'historique et dans tout signet posé par mégarde — et le prochain
 * rechargement le rejoue pour rien, alors qu'il est à usage unique.
 */
export function preleverJetonLancement(): string | null {
  const url = new URL(window.location.href)

  let jeton = url.searchParams.get(PARAM_LANCEMENT)
  if (jeton !== null && jeton !== '') {
    url.searchParams.delete(PARAM_LANCEMENT)
    nettoyerUrl(url)
    return jeton
  }

  // Le fragment n'est pas analysé par `URL` : il faut le lire à la main. Il
  // peut porter d'autres paramètres, qu'on préserve.
  const fragment = new URLSearchParams(url.hash.replace(/^#/, ''))
  jeton = fragment.get(PARAM_LANCEMENT)
  if (jeton === null || jeton === '') return null

  fragment.delete(PARAM_LANCEMENT)
  const reste = fragment.toString()
  url.hash = reste === '' ? '' : `#${reste}`
  nettoyerUrl(url)
  return jeton
}

/**
 * L'ONGLET ADOPTE LE COMPTE AVEC LEQUEL ON VIENT D'ENTRER.
 *
 * Sans ce geste, l'onglet n'a aucun slot actif : le fournisseur de porteur rend
 * une chaîne vide, et le widget d'identité ne se monte pas — le rail bascule en
 * état dégradé, « aucun porteur beTool », sur une session parfaitement valide.
 * Mesuré en production, ici et au kiosque.
 *
 * Le slot visé est celui de l'IDENTITÉ de la session (règle partagée du parc,
 * `alignerOngletSurIdentite`, SDK identity-runtime) — jamais « le compte par
 * défaut » : le défaut suit la dernière connexion faite à la porte, qui peut
 * être celle d'un autre compte ajouté entre-temps (production, 2026-11-04).
 *
 * DÉGRADÉ, JAMAIS BLOQUANT : si le trousseau ne répond pas, la session de ce
 * service est servie quand même et le rail se réhydratera au prochain geste.
 * Retenir quelqu'un dehors parce qu'un menu manque serait absurde.
 */
async function adopterLeCompteDeLEntree(identite: string | undefined): Promise<void> {
  try {
    const { runtimeIdentite } = await import('./runtime')
    const r = runtimeIdentite()
    if (r === null) return
    await alignerOngletSurIdentite(r, identite)
  } catch {
    // Voir ci-dessus : l'entrée ne dépend pas du trousseau.
  }
}

function nettoyerUrl(url: URL): void {
  window.history.replaceState({}, '', `${url.pathname}${url.search}${url.hash}`)
}

/**
 * Consomme le lancement et ouvre la session.
 *
 * Rend un RÉSULTAT plutôt que de lever : les quatre issues sont des états
 * légitimes de l'écran d'entrée, et chacune appelle un message différent. Les
 * fondre dans une exception unique donnerait « une erreur est survenue » —
 * c'est-à-dire rien.
 */
export async function entrerAvecLancement(jeton: string): Promise<ResultatEntree> {
  let session: { accessToken: string; identite: string | undefined }
  try {
    session = await consommerAuMoteur(jeton)
  } catch (erreur) {
    if (erreur instanceof LancementRefuseError) {
      // Le CODE du BFF part au journal du navigateur, toujours. Un refus rendu
      // sous un message unique — « lien expiré » — a coûté une soirée : un 403
      // `csrf_origine` (configuration) et un 401 `lancement_refuse` (jeton
      // rejoué) y devenaient indiscernables, et les deux envoyaient chercher
      // au mauvais endroit.
      console.warn(
        `[sign] lancement refusé par le BFF : HTTP ${erreur.statut}, code « ${erreur.code || '(aucun)'} »`,
      )
      return { ok: false, echec: { genre: 'intention_refusee', code: erreur.code } }
    }
    console.warn('[sign] lancement : appel au moteur impossible —', messageDe(erreur))
    return { ok: false, echec: { genre: 'panne', message: messageDe(erreur) } }
  }

  try {
    const ouverte = await entrerParPreuve(session.accessToken, session.identite)
    await adopterLeCompteDeLEntree(session.identite)
    return { ok: true, session: ouverte }
  } catch (erreur) {
    // 403 = la session beTool est bonne, mais il n'y a pas d'accès ICI : c'est
    // un rattachement qui manque, pas une authentification à refaire.
    if (statutDe(erreur) === 403) return { ok: false, echec: { genre: 'acces_refuse' } }
    if (statutDe(erreur) === 401) {
      return { ok: false, echec: { genre: 'porteur_mort', code: 'session_invalide' } }
    }
    return { ok: false, echec: { genre: 'panne', message: messageDe(erreur) } }
  }
}

/**
 * Consomme le laissez-passer AU MOTEUR, et rend la session beTool.
 *
 * Aucun cookie, aucun trousseau : le jeton signé se suffit. C'est ce qui rend
 * ce chemin insensible à l'origine depuis laquelle on l'appelle — contrairement
 * au BFF, dont le scellé ne voyage pas jusqu'ici.
 */
async function consommerAuMoteur(
  jeton: string,
): Promise<{ accessToken: string; identite: string | undefined }> {
  const moteur = origineMoteurIdentite()
  if (moteur === '') throw new Error('instance non raccordée à l’identité beTool')

  let reponse: Response
  try {
    reponse = await fetch(`${moteur}/api/lancements/consommer`, {
      method: 'POST',
      headers: { 'content-type': 'application/json', accept: 'application/json' },
      body: JSON.stringify({ jeton }),
    })
  } catch (erreur) {
    throw new Error(
      `moteur d’identité injoignable (${erreur instanceof Error ? erreur.message : 'inconnu'})`,
    )
  }

  const brut = (await reponse.json().catch(() => null)) as Record<string, unknown> | null
  if (!reponse.ok) {
    // Le CODE part au journal du navigateur : un rejeu (usage unique) et une
    // configuration fautive y devenaient indiscernables sous un message unique.
    const code = typeof brut?.['code'] === 'string' ? brut['code'] : ''
    throw new LancementRefuseError(reponse.status, code)
  }

  const session = brut?.['session'] as { accessToken?: unknown } | undefined
  if (typeof session?.accessToken !== 'string' || session.accessToken === '') {
    // Le moteur a consommé l'intention sans rendre de session : le dire, plutôt
    // que d'échouer plus loin sur un 401 inexplicable.
    throw new Error('le moteur n’a pas remis de session pour ce lancement')
  }
  // `identite.id` : la clé de raccordement que la plateforme rend avec la
  // session (lancement.ts) — l'identité que cet onglet doit incarner.
  const identite = (brut?.['identite'] as { id?: unknown } | undefined)?.id
  return { accessToken: session.accessToken, identite: typeof identite === 'string' ? identite : undefined }
}

/**
 * Ouvre la session pour un slot DÉJÀ présent dans le trousseau.
 *
 * Séparée de l'entrée par lancement parce que c'est le chemin du changement de
 * compte depuis le widget : le slot existe, il n'y a pas d'intention à
 * consommer. Refaire un lancement complet marcherait, mais obligerait à
 * repasser par le portail pour un geste qui doit rester dans la page.
 */
export async function entrerAvecSlot(slotRef: string): Promise<ResultatEntree> {
  let preuve
  try {
    preuve = await acheterPreuve(slotRef)
  } catch (erreur) {
    if (erreur instanceof PorteurMortError) {
      console.warn(`[sign] preuve refusée : porteur mort (${erreur.code})`)
      return { ok: false, echec: { genre: 'porteur_mort', code: erreur.code } }
    }
    console.warn('[sign] achat de preuve impossible —', messageDe(erreur))
    return { ok: false, echec: { genre: 'panne', message: messageDe(erreur) } }
  }

  try {
    // Le slot visé porte son identité : c'est elle que la session retient.
    const { runtimeIdentite } = await import('./runtime')
    const visee = (await runtimeIdentite()?.listBrowserSessions().catch(() => []))?.find(
      (s) => s.slotRef === slotRef,
    )
    return { ok: true, session: await entrerParPreuve(preuve.accessToken, visee?.identityId || undefined) }
  } catch (erreur) {
    // 403 = la preuve est bonne, mais il n'y a pas d'accès ICI : c'est un
    // rattachement qui manque, pas une authentification à refaire. Le dire
    // autrement enverrait l'utilisateur se reconnecter en boucle.
    if (statutDe(erreur) === 403) return { ok: false, echec: { genre: 'acces_refuse' } }
    // 401 : la preuve vient d'être achetée et elle est déjà refusée — porteur
    // mort entre les deux gestes, ou horloges désaccordées.
    if (statutDe(erreur) === 401) {
      return { ok: false, echec: { genre: 'porteur_mort', code: 'session_invalide' } }
    }
    return { ok: false, echec: { genre: 'panne', message: messageDe(erreur) } }
  }
}

function messageDe(erreur: unknown): string {
  return erreur instanceof Error ? erreur.message : 'erreur inconnue'
}

/**
 * Le statut HTTP, lu sur le TYPE que le client d'API garantit.
 *
 * `ApiError` normalise déjà tout ce qui vient du réseau (statut 0 quand la
 * requête n'a jamais atteint le serveur). Reconstituer le statut par
 * canard-typage sur `response.status` marcherait aussi — et se tromperait le
 * jour où la forme d'erreur du client change, sans que rien ne le signale.
 */
function statutDe(erreur: unknown): number | null {
  return erreur instanceof ApiError ? erreur.status : null
}
